import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FormCard } from "../../FormCard";
import { DetailRow, InputRow, SelectRow, TimeRow } from "../../Rows";
import ConfirmationModal from "../../ConfirmationModal";
import {
    ENERGY_MODE,
    createEmptyBand,
    createEmptyPeriod,
    dayOptions,
} from "./tariffWizardState";

const EnergyPriceStep = ({ energyMode, energy, onModeChange, onEnergyChange, currency = "", isPublic = false }) => {
    const { t } = useTranslation();
    const [pendingMode, setPendingMode] = useState(null);
    const modeCards = [
        {
            value: ENERGY_MODE.FIXED,
            title: t("tariff.modeFixedTitle"),
            description: t("tariff.modeFixedDescription"),
        },
        {
            value: ENERGY_MODE.TIME,
            title: t("tariff.modeTimeTitle"),
            description: t("tariff.modeTimeDescription"),
        },
        {
            value: ENERGY_MODE.POWER,
            title: t("tariff.modePowerTitle"),
            description: t("tariff.modePowerDescription"),
        },
    ];
    if (!isPublic) {
        modeCards.push({
            value: ENERGY_MODE.SPOT,
            title: t("tariff.modeSpotTitle"),
            description: t("tariff.modeSpotDescription"),
        });
    }

    const applyMode = (nextMode) => {
        let nextEnergy = energy;
        if (energyMode === ENERGY_MODE.TIME && nextMode !== ENERGY_MODE.TIME) {
            nextEnergy = { ...nextEnergy, periods: [] };
        }
        if (energyMode === ENERGY_MODE.POWER && nextMode !== ENERGY_MODE.POWER) {
            nextEnergy = { ...nextEnergy, bands: [] };
        }
        if (energyMode === ENERGY_MODE.SPOT && nextMode !== ENERGY_MODE.SPOT) {
            nextEnergy = { ...nextEnergy, energyPriceMinExclVat: "", energyPriceMaxExclVat: "" };
            if (nextMode === ENERGY_MODE.TIME || nextMode === ENERGY_MODE.POWER) {
                nextEnergy = { ...nextEnergy, price: "", vat: "" };
            }
        }
        if (nextEnergy !== energy) {
            onEnergyChange(nextEnergy);
        }
        onModeChange(nextMode);
        setPendingMode(null);
    };

    const handleModeClick = (nextMode) => {
        if (nextMode === energyMode) {
            return;
        }
        if (needsModeSwitchConfirm(energyMode, nextMode, energy)) {
            setPendingMode(nextMode);
            return;
        }
        applyMode(nextMode);
    };

    return (
        <div className="flex flex-col gap-6">
            <FormCard title={t("form.energyPrice")} description={t("tariff.energyPriceDescription")}>
                <div className={`grid grid-cols-1 gap-4 ${isPublic ? "md:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-4"}`}>
                    {modeCards.map((card) => {
                        const selected = energyMode === card.value;
                        return (
                            <button
                                key={card.value}
                                type="button"
                                onClick={() => handleModeClick(card.value)}
                                className={`text-left rounded-md border p-4 transition ${
                                    selected
                                        ? "border-primary-600 bg-primary-50"
                                        : "border-mute-200 hover:border-mute-400"
                                }`}
                            >
                                <div className="font-semibold">{card.title}</div>
                                <p className="text-sm text-mute-600 mt-1">{card.description}</p>
                            </button>
                        );
                    })}
                </div>
            </FormCard>

            {energyMode === ENERGY_MODE.FIXED && (
                <FormCard title={t("form.fixedEnergyPrice")}>
                    <EnergyPriceFields energy={energy} onEnergyChange={onEnergyChange} currency={currency} />
                </FormCard>
            )}

            {energyMode === ENERGY_MODE.SPOT && (
                <FormCard title={t("form.spotEnergyPrice")} description={t("tariff.spotEnergyPriceDescription")}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                        <PriceVatFields
                            price={energy.price}
                            vat={energy.vat}
                            currency={currency}
                            priceLabel={t("form.spotSurchargeExcl")}
                            priceTooltip={t("tariff.tooltip.spotSurcharge")}
                            vatTooltip={t("tariff.tooltip.spotVat")}
                            showPriceInclVat={false}
                            onPriceChange={(value) => onEnergyChange({ ...energy, price: value })}
                            onVatChange={(value) => onEnergyChange({ ...energy, vat: value })}
                        />
                        <InputRow
                            label={t("form.energyPriceMinExcl")}
                            tooltip={t("tariff.tooltip.energyPriceMin")}
                            value={energy.energyPriceMinExclVat}
                            isNumber
                            required={false}
                            onChange={(value) =>
                                onEnergyChange({ ...energy, energyPriceMinExclVat: value })
                            }
                        />
                        <InputRow
                            label={t("form.energyPriceMaxExcl")}
                            tooltip={t("tariff.tooltip.energyPriceMax")}
                            value={energy.energyPriceMaxExclVat}
                            isNumber
                            required={false}
                            onChange={(value) =>
                                onEnergyChange({ ...energy, energyPriceMaxExclVat: value })
                            }
                        />
                        <DetailRow
                            label={t("form.spotCustomerPriceExcl")}
                            value={formatSpotCalculationExcl(energy.price, currency, t)}
                        />
                        <DetailRow
                            label={t("form.spotCustomerPriceIncl")}
                            value={formatSpotCalculationIncl(energy.price, energy.vat, currency, t)}
                        />
                        <DetailRow
                            label={t("form.energyPriceMinIncl")}
                            value={formatPriceInclVat(
                                energy.energyPriceMinExclVat,
                                energy.vat,
                                currency
                            )}
                        />
                        <DetailRow
                            label={t("form.energyPriceMaxIncl")}
                            value={formatPriceInclVat(
                                energy.energyPriceMaxExclVat,
                                energy.vat,
                                currency
                            )}
                        />
                    </div>
                    {(energy.energyPriceMinExclVat === "" ||
                        energy.energyPriceMinExclVat == null ||
                        energy.energyPriceMaxExclVat === "" ||
                        energy.energyPriceMaxExclVat == null) && (
                        <p className="text-sm text-warning-700 mt-4">{t("tariff.spotNegativeWarning")}</p>
                    )}
                </FormCard>
            )}

            {energyMode === ENERGY_MODE.TIME && (
                <>
                <FormCard
                    title={t("form.fallback")}
                    description={t("tariff.fallbackTimeDescription")}
                >
                    <EnergyPriceFields energy={energy} onEnergyChange={onEnergyChange} currency={currency} />
                </FormCard>
                <FormCard
                    title={t("form.timePeriods")}
                    description={t("tariff.timePeriodsDescription")}
                >
                    <button
                        type="button"
                        className="btn btn-outline-primary mb-4"
                        onClick={() =>
                            onEnergyChange({
                                ...energy,
                                periods: [...energy.periods, createEmptyPeriod()],
                            })
                        }
                    >
                        {t("common.addPeriod")}
                    </button>

                    <div className="flex flex-col gap-6">
                        {energy.periods.map((period, index) => (
                            <div key={period.clientId} className="border border-mute-200 rounded-md p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold">{t("common.period", { n: index + 1 })}</h4>
                                    <button
                                        type="button"
                                        title={t("common.remove")}
                                        onClick={() =>
                                            onEnergyChange({
                                                ...energy,
                                                periods: energy.periods.filter((item) => item.clientId !== period.clientId),
                                            })
                                        }
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="text-error-400" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                                    <SelectRow
                                        label={t("tariff.days")}
                                        value={period.days}
                                        options={dayOptions(t)}
                                        isMulti
                                        required={false}
                                        onChange={(days) =>
                                            updatePeriod(energy, onEnergyChange, period.clientId, {
                                                days: days || [],
                                            })
                                        }
                                    />
                                    <TimeRow
                                        label={t("form.startTime")}
                                        value={period.startTime}
                                        onChange={(value) =>
                                            updatePeriod(energy, onEnergyChange, period.clientId, {
                                                startTime: value,
                                            })
                                        }
                                    />
                                    <TimeRow
                                        label={t("form.endTime")}
                                        value={period.endTime}
                                        onChange={(value) =>
                                            updatePeriod(energy, onEnergyChange, period.clientId, {
                                                endTime: value,
                                            })
                                        }
                                    />
                                    <PriceVatFields
                                        price={period.price}
                                        vat={period.vat}
                                        currency={currency}
                                        onPriceChange={(value) =>
                                            updatePeriod(energy, onEnergyChange, period.clientId, {
                                                price: value,
                                            })
                                        }
                                        onVatChange={(value) =>
                                            updatePeriod(energy, onEnergyChange, period.clientId, {
                                                vat: value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </FormCard>
                </>
            )}

            {energyMode === ENERGY_MODE.POWER && (
                <>
                <FormCard
                    title={t("form.fallback")}
                    description={t("tariff.fallbackPowerDescription")}
                >
                    <EnergyPriceFields energy={energy} onEnergyChange={onEnergyChange} currency={currency} />
                </FormCard>
                <FormCard
                    title={t("form.powerBands")}
                    description={t("tariff.powerBandsDescription")}
                >
                    <button
                        type="button"
                        className="btn btn-outline-primary mb-4"
                        onClick={() =>
                            onEnergyChange({
                                ...energy,
                                bands: [...energy.bands, createEmptyBand()],
                            })
                        }
                    >
                        {t("tariff.addPowerBand")}
                    </button>
                    <div className="flex flex-col gap-6">
                        {energy.bands.map((band, index) => (
                            <div key={band.clientId} className="border border-mute-200 rounded-md p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="font-semibold">{t("tariff.bandN", { n: index + 1 })}</h4>
                                    {energy.bands.length > 1 && (
                                        <button
                                            type="button"
                                            title={t("common.remove")}
                                            onClick={() =>
                                                onEnergyChange({
                                                    ...energy,
                                                    bands: energy.bands.filter((item) => item.clientId !== band.clientId),
                                                })
                                            }
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="text-error-400" />
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
                                    <InputRow
                                        label={t("form.minPowerKw")}
                                        value={band.minPower}
                                        isNumber
                                        onChange={(value) =>
                                            updateBand(energy, onEnergyChange, band.clientId, {
                                                minPower: value,
                                            })
                                        }
                                    />
                                    <InputRow
                                        label={t("form.maxPowerKwBand")}
                                        value={band.maxPower}
                                        isNumber
                                        onChange={(value) =>
                                            updateBand(energy, onEnergyChange, band.clientId, {
                                                maxPower: value,
                                            })
                                        }
                                    />
                                    <PriceVatFields
                                        price={band.price}
                                        vat={band.vat}
                                        currency={currency}
                                        onPriceChange={(value) =>
                                            updateBand(energy, onEnergyChange, band.clientId, {
                                                price: value,
                                            })
                                        }
                                        onVatChange={(value) =>
                                            updateBand(energy, onEnergyChange, band.clientId, {
                                                vat: value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </FormCard>
                </>
            )}
            <ConfirmationModal
                isOpen={pendingMode != null}
                title={modeSwitchTitle(energyMode, pendingMode, t)}
                description={modeSwitchDescription(energyMode, pendingMode, t)}
                onClose={() => setPendingMode(null)}
                onConfirm={() => applyMode(pendingMode)}
            />
        </div>
    );
};

function hasFilledPeriods(energy) {
    return (energy.periods || []).some(
        (period) =>
            (period.days || []).length ||
            period.startTime ||
            period.endTime ||
            period.price !== ""
    );
}

function hasFilledBands(energy) {
    return (energy.bands || []).some(
        (band) => band.minPower !== "" || band.maxPower !== "" || band.price !== ""
    );
}

function hasFilledSpot(energy) {
    return (
        energy.price !== "" ||
        energy.energyPriceMinExclVat !== "" ||
        energy.energyPriceMaxExclVat !== ""
    );
}

function needsModeSwitchConfirm(fromMode, toMode, energy) {
    if (fromMode === toMode) {
        return false;
    }
    if (toMode === ENERGY_MODE.SPOT) {
        return true;
    }
    if (fromMode === ENERGY_MODE.TIME) {
        return hasFilledPeriods(energy);
    }
    if (fromMode === ENERGY_MODE.POWER) {
        return hasFilledBands(energy);
    }
    if (fromMode === ENERGY_MODE.SPOT) {
        return hasFilledSpot(energy);
    }
    return false;
}

function modeSwitchTitle(fromMode, toMode, t) {
    if (fromMode === ENERGY_MODE.SPOT) {
        if (toMode === ENERGY_MODE.TIME || toMode === ENERGY_MODE.POWER) {
            return t("confirm.switchFromSpotToTimeOrPower");
        }
        return t("confirm.switchFromSpot");
    }
    if (toMode === ENERGY_MODE.SPOT) {
        return t("confirm.switchToSpot");
    }
    if (fromMode === ENERGY_MODE.TIME) {
        if (toMode === ENERGY_MODE.POWER) {
            return t("confirm.switchToPowerBased");
        }
        return t("confirm.switchToFixedFromTime");
    }
    if (toMode === ENERGY_MODE.TIME) {
        return t("confirm.switchToTimeBased");
    }
    return t("confirm.switchToFixedFromPower");
}

function modeSwitchDescription(fromMode, toMode, t) {
    if (
        (fromMode === ENERGY_MODE.TIME && toMode === ENERGY_MODE.POWER) ||
        (fromMode === ENERGY_MODE.POWER && toMode === ENERGY_MODE.TIME)
    ) {
        return t("confirm.onlyOneTimeOrPower");
    }
    return t("confirm.onlyOneEnergyModel");
}

function EnergyPriceFields({ energy, onEnergyChange, currency = "" }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
            <PriceVatFields
                price={energy.price}
                vat={energy.vat}
                currency={currency}
                onPriceChange={(value) => onEnergyChange({ ...energy, price: value })}
                onVatChange={(value) => onEnergyChange({ ...energy, vat: value })}
            />
        </div>
    );
}

function PriceVatFields({
    price,
    vat,
    currency,
    onPriceChange,
    onVatChange,
    priceLabel,
    priceTooltip,
    vatTooltip,
    showPriceInclVat = true,
}) {
    const { t } = useTranslation();
    return (
        <>
            <InputRow
                label={priceLabel || t("form.pricePerKwhExcl")}
                tooltip={priceTooltip || t("tariff.tooltip.price")}
                value={price}
                isNumber
                onChange={onPriceChange}
            />
            <InputRow
                label={t("form.vatPercent")}
                tooltip={vatTooltip || t("tariff.tooltip.vat")}
                value={vat}
                isNumber
                onChange={onVatChange}
            />
            {showPriceInclVat && (
                <DetailRow
                    label={t("form.pricePerKwhIncl")}
                    value={formatPriceInclVat(price, vat, currency)}
                />
            )}
        </>
    );
}

export function formatPriceInclVat(price, vat, currency) {
    const excl = Number(price);
    if (price === "" || price == null || !Number.isFinite(excl)) {
        return "";
    }
    const vatPct = vat === "" || vat == null ? 0 : Number(vat);
    if (!Number.isFinite(vatPct)) {
        return "";
    }
    const incl = excl * (1 + vatPct / 100);
    const amount = formatPriceAmount(incl);
    return currency ? `${amount} ${currency}` : amount;
}

function formatPriceAmount(value) {
    const rounded = Math.round(value * 10000) / 10000;
    if (Number.isInteger(rounded)) {
        return String(rounded);
    }
    return String(rounded);
}

function formatSpotSurchargeTerm(price, currency, t) {
    const formatted = formatPriceInclVat(price, 0, currency);
    if (!formatted) {
        return t("tariff.spotSurchargeTerm");
    }
    return formatted;
}

export function formatSpotCalculationExcl(price, currency, t) {
    return t("tariff.spotCalculationExcl", {
        surcharge: formatSpotSurchargeTerm(price, currency, t),
    });
}

export function formatSpotCalculationIncl(price, vat, currency, t) {
    const vatPct = vat === "" || vat == null ? 0 : Number(vat);
    if (!Number.isFinite(vatPct)) {
        return "";
    }
    return t("tariff.spotCalculationIncl", {
        surcharge: formatSpotSurchargeTerm(price, currency, t),
        vatFactor: formatPriceAmount(1 + vatPct / 100),
    });
}

function updatePeriod(energy, onEnergyChange, clientId, patch) {
    onEnergyChange({
        ...energy,
        periods: energy.periods.map((period) =>
            period.clientId === clientId ? { ...period, ...patch } : period
        ),
    });
}

function updateBand(energy, onEnergyChange, clientId, patch) {
    onEnergyChange({
        ...energy,
        bands: energy.bands.map((band) =>
            band.clientId === clientId ? { ...band, ...patch } : band
        ),
    });
}

export default EnergyPriceStep;
